import React, { useState } from 'react';
import { toast } from 'sonner';
import { isIosApp } from '../utils/platform';
import { authAPI } from '../utils/api';
import { metaCompleteRegistration } from '../utils/metaAppEvents';

// Native iOS "Sign in with Apple" — lets a user create an account / sign in WITHOUT a phone
// number (Apple Guideline 5.1.1(v)). Renders ONLY inside the native iOS app; web & Android
// keep phone + PIN. The plugin is loaded lazily so web/Android bundles never touch native code.
export const AppleSignInButton = ({ onSignedIn, country, currency }) => {
  const [busy, setBusy] = useState(false);
  if (!isIosApp()) return null;

  const handle = async () => {
    setBusy(true);
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const ch = await authAPI.appleChallenge();
      const { nonce, challenge_id } = ch.data;
      const result = await SignInWithApple.authorize({
        clientId: 'com.moneyfeed.app',
        redirectURI: '',
        scopes: 'email name',
        nonce,
        state: challenge_id,
      });
      const r = result.response || {};
      const res = await authAPI.appleLogin({
        identityToken: r.identityToken,
        challenge_id,
        email: r.email || null,
        givenName: r.givenName || null,
        familyName: r.familyName || null,
        country,
        currency,
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      if (res.data.is_new) metaCompleteRegistration('apple');
      onSignedIn?.(res.data.user);
    } catch (e) {
      const msg = String(e?.message || '').toLowerCase();
      if (!msg.includes('cancel')) toast.error('Apple sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      data-testid="apple-signin-btn"
      className="w-full flex items-center justify-center gap-2 bg-black text-white rounded-xl py-3 font-semibold hover:bg-black/90 transition-colors disabled:opacity-60"
    >
      <svg viewBox="0 0 384 512" className="h-4 w-4 fill-current" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
      </svg>
      {busy ? 'Signing in…' : 'Sign in with Apple'}
    </button>
  );
};

export default AppleSignInButton;
